import React from 'react'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  metadataEditableCss,
  formLabelCss,
  readOnlyGridCss,
  metadataCss
} from '../text'
import { CsPreviousBackIcon, CsNextIcon } from '../icons'
import { DayzedDate, DayzedWeek, DatePropsGetter } from './shared'

export const calendarBackButtonTestID = 'calendar-back-button-test-id'
export const calendarForwardButtonTestID = 'calendar-forward-button-test-id'
export const getUniqueDayKeyFromDate = (date: Date) =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

export function Month({ children }) {
  return (
    <div
      className={css`
        flex: 0 0 196px;
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        margin: 25px 15px;
      `}
      children={children}
    />
  )
}

interface MonthHeaderProps {
  readonly month: string
  readonly year: string
  readonly dayAbbreviations: string[]
}

export function MonthHeader({
  month,
  year,
  dayAbbreviations
}: MonthHeaderProps) {
  return (
    <div
      className={css`
        text-align: center;
      `}
    >
      <div>
        <span className={formLabelCss}>{month}</span>{' '}
        <span className={readOnlyGridCss}>{year}</span>
      </div>
      <div
        className={cx(
          'legacy-ds-date-picker-month-header',
          css`
            display: flex;
            border-bottom: ${theme.color.neutral40} 1px solid;
            margin-top: 4px;
          `
        )}
      >
        {dayAbbreviations.map((day, index) => {
          return (
            <span
              key={`day-${index}`}
              className={cx(
                metadataCss,
                css`
                  flex: 1;
                  padding: 8px 0;
                `
              )}
            >
              {day}
            </span>
          )
        })}
      </div>
    </div>
  )
}

interface WeekProps {
  readonly week: DayzedWeek
  readonly getDateProps: DatePropsGetter
  readonly isDateSelected: (date: DayzedDate) => boolean
  readonly isDateHovered: (date: DayzedDate) => boolean
  readonly isDateBeginningOfSequence: (date: DayzedDate) => boolean
  readonly isDateEndOfSequence: (date: DayzedDate) => boolean
  readonly onDateMouseEnter?: (date: DayzedDate) => void
  readonly onDateMouseLeave?: (date: DayzedDate) => void
}

export function Week({
  week,
  getDateProps,
  isDateSelected,
  isDateHovered,
  isDateBeginningOfSequence,
  isDateEndOfSequence,
  onDateMouseEnter,
  onDateMouseLeave
}: WeekProps) {
  return (
    <div
      className={css`
        display: flex;
        label: week;
      `}
    >
      {week.map((dateObj, dayIndex) => {
        if (!dateObj || typeof dateObj === 'string') {
          return (
            <div
              key={`empty-day-${dayIndex}`}
              className={css`
                flex: 1;
              `}
            />
          )
        }

        const { date, selectable } = dateObj
        const dayKey = getUniqueDayKeyFromDate(date)
        const isBeginningOfSequence = isDateBeginningOfSequence(dateObj)
        const isEndOfSequence = isDateEndOfSequence(dateObj)
        const isHovered = isDateHovered(dateObj)
        const isSelected = isDateSelected(dateObj)
        return (
          <div
            key={dayKey}
            data-testid={dayKey}
            data-selectable={selectable}
            data-hovered={isDateHovered(dateObj)}
            data-selected={isDateSelected(dateObj)}
            {...getDateProps({
              dateObj,
              onMouseEnter: () => onDateMouseEnter && onDateMouseEnter(dateObj),
              onMouseLeave: () => onDateMouseLeave && onDateMouseLeave(dateObj),
              className: cx(
                'legacy-ds-date-picker-day',
                css`
                  flex: 1;
                  margin: 4px 0;
                  padding: 4px 0;
                  display: flex;
                  justify-content: center;
                  flex-direction: column;
                  text-align: center;
                  label: day;
                `,
                metadataEditableCss,
                {
                  [css`
                    color: ${theme.color.neutral40};
                    cursor: default;
                  `]: !selectable
                },
                {
                  [css`
                    color: ${theme.color.white};
                  `]: isSelected
                },
                {
                  [cx(
                    'legacy-ds-date-picker-day--hover',
                    css`
                      background-color: ${theme.color.teal10};
                    `
                  )]: isHovered
                },
                {
                  [cx(
                    'legacy-ds-date-picker-day--selected',
                    css`
                      background-color: ${theme.color.turquoise70};
                    `
                  )]: isSelected
                },
                {
                  [css`
                    border-top-left-radius: 3px;
                    border-bottom-left-radius: 3px;
                  `]: isBeginningOfSequence
                },
                {
                  [css`
                    border-top-right-radius: 3px;
                    border-bottom-right-radius: 3px;
                  `]: isEndOfSequence
                }
              )
            })}
          >
            {date.getDate()}
          </div>
        )
      })}
    </div>
  )
}

interface CalendarButtonProps {
  readonly getButtonProps: (props: React.HTMLProps<HTMLButtonElement>) => any
}

function CalendarButton({
  getButtonProps,
  renderButton,
  className,
  ...props
}: CalendarButtonProps & {
  readonly renderButton: (renderButtonProps: {
    readonly disabled?: boolean
  }) => React.ReactNode
  readonly className?: string
  readonly 'data-testid': string
}) {
  const buttonProps = getButtonProps({
    className: css`
      border: none;
      margin: 0;
      padding: 0;
      width: auto;
      overflow: visible;
      background: transparent;
      color: inherit;
      font: inherit;
      line-height: normal;
      &:focus {
        outline: none;
      }
    `
  })
  return (
    <div className={className}>
      <button {...props} {...buttonProps} type="button">
        {renderButton({ disabled: buttonProps.disabled })}
      </button>
    </div>
  )
}

export function CalendarBackButton(props: CalendarButtonProps) {
  return (
    <CalendarButton
      {...props}
      data-testid={calendarBackButtonTestID}
      className={css`
        position: absolute;
        left: 18px;
        top: 22px;
      `}
      renderButton={({ disabled }) => {
        return (
          !disabled && (
            <CsPreviousBackIcon
              className={css`
                color: ${theme.color.neutral80};
                font-size: 22px;
              `}
            />
          )
        )
      }}
    />
  )
}

export function CalendarForwardButton(props: CalendarButtonProps) {
  return (
    <CalendarButton
      {...props}
      data-testid={calendarForwardButtonTestID}
      className={css`
        position: absolute;
        right: 18px;
        top: 22px;
      `}
      renderButton={({ disabled }) => {
        return (
          !disabled && (
            <CsNextIcon
              className={css`
                color: ${theme.color.neutral80};
                font-size: 22px;
              `}
            />
          )
        )
      }}
    />
  )
}
